<template>
  <div class="group-detail">
    <h1 class="common__main-title">Create Group</h1>
    <div class="common__main-contener">
      <div class="main-content">
        <div class="content-top mb-2">
          <b-row>
            <b-col cols="4">
              <h3 class="common__search-title">Fiscal Year Semester</h3>
              <FiscalYearSemesterInput v-model="selectedYearSemester" />
            </b-col>
              <b-col cols="4">
                <h3 class="common__search-title">Name</h3>
                <select v-model="selectedAssignmentName" v-if="assignmentNames.length > 0"
                  :disabled="false">
                  <option :value="null"></option>
                  <option v-for="(name, index) of assignmentNames" :key="index" :value="name">
                      {{ `${name}` }}
                  </option>
                </select>
                <div v-else>
                  None
                </div>
              </b-col>
              <b-col cols="4" class="d-flex align-items-end">
                <b-button pill size="sm" variant="secondary"
                  :disabled="!selectedAssignmentName"
                  @click="selectAssignment">
                  Select
                </b-button>
              </b-col>
          </b-row>
          <div class="mt-4 px-5 d-flex justify-content-end">
            <b-button
              pill
              variant="primary"
              size="sm"
              @click="showAddGroupModal()"
            >
              New Assignment
            </b-button>
          </div>
        </div>
        <div class="content-bottom" v-if="selectedAssignment !== null">
          <b-container>
            <b-row>
              <b-col>
                <hr >
              </b-col>
            </b-row>
            <b-row class="mb-5">
              <b-col>
                <label class="font-weight-bold">Fiscal Year</label>
                <div>{{ selectedAssignment.year }}</div>
              </b-col>
              <b-col>
                <label class="font-weight-bold">Semester</label>
                <div>{{ selectedAssignment.semester.name }}</div>
              </b-col>
              <b-col>
                <label class="font-weight-bold">Name</label>
                <div>{{ selectedAssignment.name }}</div>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="5">
                  <div class="item">
                    <div v-for="stage of nextSteps" :key="stage.step"
                      :disabled="step < stage.step"
                    >
                      <div class="check-item">
                        <div
                          class="check-text"
                          @click="confirmReturnStep(stage)"
                        >
                          <font-awesome-icon
                            v-if="stage.isImporting"
                            spin
                            class="check-circle"
                            :icon="['fas', 'spinner']"
                            :style="{ color: step >= stage.step ? '#2E75B5' : 'gray' }"
                          />
                          <font-awesome-icon
                            v-else
                            class="check-circle"
                            :icon="['far', step > stage.step ? 'check-circle': 'circle']"
                            :style="{ color: step >= stage.step ? '#2E75B5' : 'gray' }"
                          />
                          <div
                            class="stage-name"
                            :style="{
                              color: step >= stage.step ? '#2E75B5' : 'gray',
                              'font-weight': step == stage.step ? 'bold' : 'normal',
                            }"
                          >
                            {{ stage.name }}
                          </div>
                        </div>
                        <div
                          class="d-flex mt-3"
                          v-if="step === stage.step + 1"
                        >
                          <b-button
                            v-if ="stage.warnings"
                            pill
                            variant="warning"
                            size="sm"
                            class="mr-2"
                            @click="showWarningModal()"
                          >
                            Warning
                          </b-button>
                          <b-button
                            v-if ="stage.DownloadCsv"
                            pill
                            variant="info"
                            size="sm"
                            @click="downloadCSV(stage.DownloadCsv)"
                          >
                            download CSV
                          </b-button>
                          <!-- 成功時のモーダル -->
                          <b-modal id="success-modal" :show="true" ok-only centered>
                            <template v-if="stage.warnings && stage.warnings.length > 0">
                              <h5>Warning</h5>
                              <b-alert variant="warning" show>
                                <div
                                  v-for="warn of stage.warnings"
                                  :key="warn.index"
                                  class="text-left"
                                >
                                  {{ warn }}
                                </div>
                              </b-alert>
                            </template>
                          </b-modal>
                        </div>
                      </div>
                    </div>
                  </div>
                </b-col>
                <b-col cols="7">
                  <component
                    ref="formComponent"
                    :is="nextStepComponent"
                    :selectedAssignment="selectedAssignment"
                    @complete="importing(step, true)"
                  />
              </b-col>
            </b-row>
          </b-container>
        </div>
      </div>
    </div>
    <loading :isLoading="isLoading" />
    <ConfirmModal ref="confirmModal" isLoadingMode @ok="returnStep"/>
    <AddGroupModal ref="addModal" isLoadingMode @ok="createNewGroupAssignment" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import _ from 'lodash'
import FiscalYearSemesterInput from '@/components/parts/inputItem/FiscalYearSemesterInput.vue'
import GroupNameInput from '@/components/parts/inputItem/GroupNameInput.vue'
import ImportAcademicCalendar from '@/components/parts/group/import/AcademicCalendar.vue';
import GroupAssignment from '@/components/parts/group/import/GroupAssignment.vue';
import ImportGroup from '@/components/parts/group/import/ImportGroup.vue';
import AssignBooth from '@/components/parts/group/import/AssignBooth.vue';
import AssignBoothEdit from '@/components/parts/group/import/AssignBoothEdit.vue';
import ReflectLMS from '@/components/parts/group/import/ReflectLMS.vue';
import AddGroupModal from '@/components/parts/modal/AddGroup.vue'

import ConfirmModal from '@/components/parts/modal/Confirm.vue';
import { FiscalYearSemester } from '@/store/common.types';
import { AssignmentStatus } from '@/store/groups/formation/types';
import Loading from '@/components/parts/modal/Loading.vue';
// @ts-expect-error 型チェック不要部分
import FileSaver from 'file-saver'

interface Stage {
  step: number
  name: string
  componentName: string
  isImporting: boolean
  checkImportActionName: string
  warnings?: string[]|null
  DownloadCsv?: null
}

const initialNextSteps = (): Stage[] => {
  return [
    {
      step: 0,
      name: 'Academic Calendar',
      componentName: 'ImportAcademicCalendar',
      isImporting: false,
      checkImportActionName: 'checkImportAcademicCalendarByCsv',
    },
    {
      step: 1,
      name: 'Group Assignment',
      componentName: 'GroupAssignment',
      isImporting: false,
      checkImportActionName: 'checkAssignGroupByCsv',
      warnings: null,
      DownloadCsv: null,
    },
    {
      step: 2,
      name: 'Group Import',
      componentName: 'ImportGroup',
      isImporting: false,
      checkImportActionName: 'checkImportGroupByCsv',
      warnings: null,
    },
    {
      step: 3,
      name: 'Booth Assignment',
      componentName: 'AssignBooth',
      isImporting: false,
      checkImportActionName: 'checkAssignGroupBooths',
      warnings: null,
      DownloadCsv: null,
    },
    {
      step: 4,
      name: 'Booth Assignment Edit',
      componentName: 'AssignBoothEdit',
      isImporting: false,
      checkImportActionName: 'checkAssignBoothsEdit',
    },
    {
      step: 5,
      name: 'Reflect Section',
      componentName: 'ReflectLMS',
      isImporting: false,
      checkImportActionName: 'checkSendGroupsToLms',
    },
  ]
}

export default Vue.extend({
  components: {
    FiscalYearSemesterInput,
    GroupNameInput,
    ImportAcademicCalendar,
    GroupAssignment,
    ImportGroup,
    AssignBooth,
    AssignBoothEdit,
    ReflectLMS,
    AddGroupModal,
    ConfirmModal,
    Loading,
  },

  data(): {
    selectedYearSemester: FiscalYearSemester|null,
    selectedAssignmentName: string|null,
    selectedAssignment: AssignmentStatus|null,
    step: number|undefined,
    backStep: number|undefined,
    nextSteps: Stage[],
    isLoading: boolean,
    } {
    return {
      selectedYearSemester: null,
      selectedAssignmentName: null,
      step: undefined,
      selectedAssignment: null,
      backStep: undefined,
      nextSteps: initialNextSteps(),
      isLoading: false,
    };
  },
  computed: {
    nextStepComponent(): string {
      const stage = _.find(this.nextSteps, { step: this.step }) as Stage|undefined
      if (stage) {
        return stage.componentName
      }
      return ''
    },
    /**
     * グループ編成名称の一覧
     */
    assignmentNames(): string[] {
      return _.chain(this.$store.state.groups.formation.assignments)
        // 選択中の年度楽器に紐づくグループ編成名を抽出
        .filter({
          year: this.selectedYearSemester?.fiscalYear,
          semester: this.selectedYearSemester?.semester,
        })
        .map('name')
        // 新規作成用に選択中の名称を追加（nullの場合を考慮してcompactも実施）
        .push(this.selectedAssignmentName)
        .compact()
        .uniq()
        .value()
    },
  },

  created() {
    this.isLoading = true
    this.$store.dispatch('fetchGroupAssignments')
      .finally(() => { this.isLoading = false })
  },

  methods: {
    /**
     * グループ編成を新しく追加するモーダルの表示
     */
    showAddGroupModal() {
      (this.$refs.addModal as any).open()
    },

    /**
     * グループ編成を新しく追加
     */
    createNewGroupAssignment(assignment: AssignmentStatus) {
      this.$set(this.$data, 'nextSteps', initialNextSteps())
      this.selectedAssignment = assignment
      this.step = assignment.step
    },

    /**
     * グループ編成を選択する
     */
    selectAssignment() {
      this.$set(this.$data, 'nextSteps', initialNextSteps())
      this.selectedAssignment = this.$store.getters
        .findGroupAssignmentStatus(
          this.selectedYearSemester?.fiscalYear,
          this.selectedYearSemester?.semester,
          this.selectedAssignmentName,
        ) || null
      this.step = this.selectedAssignment?.step
      this.importing(this.step, false)
    },

    /**
     * stepを戻る場合の確認
     */
    confirmReturnStep(backStage: Stage): void {
      if (!this.step) return
      if (this.step as number <= backStage.step) return
      const maxStep = 5
      if (this.step === maxStep) {
        this.$bvModal.msgBoxOk('You cannot go back to the previous step.')
        return
      }
      this.backStep = backStage.step;
      (this.$refs.confirmModal as ConfirmModal).open(
        `Back to the ${backStage.name}?
        ※ The system only saves the data at and before the current stage after execution.`,
      )
    },

    /**
     * stepを戻す
     */
    returnStep(): void {
      this.$set(this.$data, 'nextSteps', initialNextSteps())
      this.step = this.backStep;
      (this.$refs.confirmModal as ConfirmModal).close();
    },

    /**
     * import処理開始
     */
    importing(step: number|undefined, isFirst: boolean): void {
      if (step === undefined) return

      // 初回インポート直後の場合ローディングし、処理を止める
      if (isFirst) {
        this.$set(this.nextSteps[step], 'isImporting', true)
        return
      }

      // 上記以外の場合、チェックAPIを叩いて現状確認。
      this.step = step - 1
      this.nextSteps[this.step].isImporting = true
      this.$store.dispatch(`${this.nextSteps[this.step].checkImportActionName}`, this.selectedAssignment)
        .then((res) => {
          if (this.step === undefined) return
          if (res.tutorAssignmentCsv) {
            this.$set(this.nextSteps[this.step], 'DownloadCsv', res.tutorAssignmentCsv)
          }
          if (res.boothAssignmentCsv) {
            this.$set(this.nextSteps[this.step], 'DownloadCsv', res.boothAssignmentCsv)
          }
          if (res.warnings && res.warnings.length) {
            this.$set(this.nextSteps[this.step], 'warnings', res.warnings)
          }
          this.$set(this.nextSteps[this.step], 'isImporting', false)
          this.nextStep()
        })
        .catch((error) => {
          if (this.step === undefined) return
          this.$set(this.nextSteps[this.step], 'isImporting', false)
          this.$bvModal.msgBoxOk('error');
          (this.$refs.formComponent as any).checkError(error)
        })
    },

    /**
     * 次のステップに進む
     */
    nextStep(): void {
      if (this.isLoading) return
      this.isLoading = true
      this.$store.dispatch('fetchGroupAssignments')
        .then(() => {
          this.syncAsignmentStepWithStore()
        })
        .finally(() => { this.isLoading = false })
    },

    /**
     * storeのstep情報を取得する
     */
    syncAsignmentStepWithStore(): void {
      // 対象のグループ編成がnullの場合、表示を取り消す。
      if (!this.selectedAssignment) {
        this.selectedAssignment = null
        this.step = undefined
        return
      }
      this.selectedAssignment = this.$store.getters
        .findGroupAssignmentStatus(
          this.selectedAssignment.year,
          this.selectedAssignment.semester,
          this.selectedAssignment.name,
        ) || null
      this.step = this.selectedAssignment?.step
    },

    /**
     * csvのダウンロード
     */
    downloadCSV(csv: {
      charaset: string
      content: string
      filename: string
    }) {
      try {
        const blob = new Blob([csv.content], { type: 'text/plain;charset=utf-8' });
        FileSaver.saveAs(blob, csv.filename);
        this.isLoading = false;
      } catch (error: any) {
        this.isLoading = false;
        const message = _.has(error, 'response') ? error.response.data.message : error.message;
        this.$bvModal.msgBoxOk(`Error - ${message}`)
      }
    },

    /**
     * 警告モーダルの表示処理
     */
    showWarningModal() {
      this.$bvModal.show('success-modal')
    },
  },

  watch: {
    selectedYearSemester: {
      deep: true,
      handler() {
        this.selectedAssignmentName = null
      },
    },
  },
})
</script>

<style scoped>
input[type="text"], select{
  padding: 5px;
  line-height: 0;
  border: 1px solid #AAAFB6;
  border-radius: 30px;
}

select {
  padding: 5px;
  border: 1px solid #AAAFB6;
  border-radius: 30px;
  width: 100%;
}

.main-content {
  background: #fff;
  padding: 20px;
  border-radius: 20px;
  margin-bottom:20px;
}

.content-top {
  text-align: center;
}

.content-bottom {
  display: flex;
}

.stage-name {
  font-size: 1.5em;
  color: gray;
}

.button-container {
  display: flex;
  justify-content: space-between;
}

.check-circle {
  width: 30px;
  height: 30px;
  margin-right:10px;
}

.check-item {
  margin-bottom:70px;
  /* color: #2E75B5; */
}

.check-text {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.check-item p {
  font-weight: bold;
  font-size: 25px;
}
</style>
